import React, { useEffect, useState, useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Layout,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import {
  canViewTaxDeclaration,
  canCreateTaxDeclaration,
  canEditTaxDeclaration,
  canDeleteTaxDeclaration,
  isSuperAdmin
} from '../utils/permissions';
import taxDeclarationService from '../services/taxDeclarationService';
import organizationService from '../services/organizationService';
import employeeService from '../services/employeeService';

const DEDUCTION_FIELDS = [
  { name: 'sec80c', label: 'Section 80C - Life Insurance Premium', placeholder: 'Enter amount' },
  { name: 'sec80d', label: 'Section 80D - Health Insurance Premium', placeholder: 'Enter amount' },
  { name: 'sec80e', label: 'Section 80E - Education Loan Interest', placeholder: 'Enter amount' },
  { name: 'sec80ccd', label: 'Section 80CCD - Pension Contribution', placeholder: 'Enter amount' },
  { name: 'lta', label: 'Leave Travel Allowance', placeholder: 'Enter amount' },
  { name: 'hra', label: 'House Rent Allowance', placeholder: 'Enter amount' },
  { name: 'sec24b', label: 'Section 24B - Home Loan Interest', placeholder: 'Enter amount' }
];

const emptyValues = {
  fy: '',
  regime: 'new',
  sec80c: 0,
  sec80d: 0,
  sec80e: 0,
  sec80ccd: 0,
  lta: 0,
  hra: 0,
  sec24b: 0
};

const formatCurrency = (value) =>
  `Rs. ${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const getTotalDeductions = (record) => {
  const fields = ['sec80c', 'sec80d', 'sec80e', 'sec80ccd', 'lta', 'hra', 'sec24b'];
  return fields.reduce((sum, field) => sum + (record[field] || 0), 0);
};

function TaxDeclaration() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [declarations, setDeclarations] = useState([]);
  const [editingDeclaration, setEditingDeclaration] = useState(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const selectedFormOrganizationId = Form.useWatch('organizationId', form);

  const canRead = canViewTaxDeclaration(user);
  const canManage = canCreateTaxDeclaration(user) || canEditTaxDeclaration(user);
  const canDelete = canDeleteTaxDeclaration(user);

  const effectiveOrganizationId = useMemo(
    () => (isSuperAdmin(user) ? (selectedOrganizationId || user?.organizationId) : user?.organizationId),
    [selectedOrganizationId, user]
  );

  // Load organizations for SuperAdmin
  useEffect(() => {
    const loadOrganizations = async () => {
      if (!isSuperAdmin(user)) {
        setSelectedOrganizationId(user?.organizationId);
        return;
      }

      try {
        const response = await organizationService.getAll(1, 1000);
        setOrganizations(response.data || []);
        if (!selectedOrganizationId) {
          setSelectedOrganizationId(user?.organizationId);
        }
      } catch (error) {
        console.error('Failed to load organizations', error);
      }
    };

    loadOrganizations();
  }, [user]);

  // Load employees for selected organization
  useEffect(() => {
    const loadEmployees = async () => {
      if (!effectiveOrganizationId) return;

      try {
        const response = await employeeService.getAll(1, 1000, {
          organizationId: effectiveOrganizationId
        });
        setEmployees(response?.data?.data || response?.data || []);
      } catch (error) {
        console.error('Failed to load employees', error);
        setEmployees([]);
      }
    };

    if (isSuperAdmin(user)) {
      loadEmployees();
    }
  }, [effectiveOrganizationId, user]);

  // Load tax declarations
  const loadTaxDeclarations = async () => {
    try {
      setLoading(true);
      const response = await taxDeclarationService.getAll({
        organizationId: effectiveOrganizationId
      });
      setDeclarations(response.data || []);
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load tax declarations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead && effectiveOrganizationId) {
      loadTaxDeclarations();
    }
  }, [canRead, effectiveOrganizationId]);

  const openCreateModal = () => {
    setEditingDeclaration(null);
    setSelectedEmployeeId(null);
    const defaultValues = {
      ...emptyValues,
      ...(isSuperAdmin(user) && { organizationId: effectiveOrganizationId })
    };
    form.setFieldsValue(defaultValues);
    setIsModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingDeclaration(record);
    setSelectedEmployeeId(record.employeeId);
    form.setFieldsValue({
      ...emptyValues,
      ...record
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      const payload = {
        ...values,
        organizationId: isSuperAdmin(user) ? values.organizationId : user?.organizationId,
        employeeId: isSuperAdmin(user) ? (values.employeeId || selectedEmployeeId) : undefined
      };

      if (editingDeclaration?.id) {
        await taxDeclarationService.update(editingDeclaration.id, payload);
        message.success('Tax declaration updated successfully');
      } else {
        await taxDeclarationService.create(payload);
        message.success('Tax declaration created successfully');
      }

      setIsModalOpen(false);
      form.resetFields();
      setSelectedEmployeeId(null);
      await loadTaxDeclarations();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save tax declaration');
    } finally {
      setSaving(false);
    }
  };

  const handleOrganizationChange = async (organizationId) => {
    setSelectedEmployeeId(null);
    form.setFieldValue('employeeId', undefined);

    if (!organizationId) {
      setEmployees([]);
      return;
    }

    try {
      setEmployeesLoading(true);
      const response = await employeeService.getAll(1, 1000, {
        organizationId: organizationId
      });
      setEmployees(response?.data?.data || response?.data || []);
    } catch (error) {
      console.error('Failed to load employees', error);
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setSaving(true);
      await taxDeclarationService.delete(id, effectiveOrganizationId);
      message.success('Tax declaration deleted successfully');
      await loadTaxDeclarations();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete tax declaration');
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) {
    return (
      <Layout style={{ padding: '20px' }}>
        <Card>
          <p>You do not have permission to view tax declarations.</p>
        </Card>
      </Layout>
    );
  }

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, record) =>
        record.employee ? `${record.employee.firstName} ${record.employee.lastName}` : '-',
      width: 150
    },
    {
      title: 'Fiscal Year',
      dataIndex: 'fy',
      key: 'fy',
      width: 120
    },
    {
      title: 'Regime',
      dataIndex: 'regime',
      key: 'regime',
      width: 100,
      render: (value) => <Tag color={value === 'old' ? 'orange' : 'blue'}>{value.toUpperCase()}</Tag>
    },
    {
      title: 'Section 80C',
      dataIndex: 'sec80c',
      key: 'sec80c',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Section 80D',
      dataIndex: 'sec80d',
      key: 'sec80d',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'HRA',
      dataIndex: 'hra',
      key: 'hra',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'LTA',
      dataIndex: 'lta',
      key: 'lta',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Total Deductions',
      key: 'total',
      align: 'right',
      render: (_, record) => formatCurrency(getTotalDeductions(record))
    },
    {
      title: 'Created By',
      key: 'createdBy',
      render: (_, record) =>
        record.createdByUser ? `${record.createdByUser.firstName} ${record.createdByUser.lastName}` : '-'
    }
  ];

  if (canManage || canDelete) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {canManage && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
              title="Edit"
            />
          )}
          {canDelete && (
            <Popconfirm
              title="Delete Tax Declaration"
              description="Are you sure you want to delete this tax declaration?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger size="small" icon={<DeleteOutlined />} title="Delete" />
            </Popconfirm>
          )}
        </Space>
      )
    });
  }

  return (
    <Layout style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Tax Declaration</h1>
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Add New Declaration
          </Button>
        )}
      </div>

      {!canManage && (
        <Alert
          type="info"
          message="This page is read-only for your role."
          style={{ marginBottom: 16 }}
        />
      )}

      {isSuperAdmin(user) && (
        <div style={{ marginBottom: '16px' }}>
          <Select
            value={effectiveOrganizationId}
            onChange={setSelectedOrganizationId}
            style={{ width: '300px' }}
            placeholder="Select Organization"
            options={organizations.map((org) => ({
              label: org.name,
              value: org.id
            }))}
          />
        </div>
      )}

      <Spin spinning={loading || saving}>
        <Card>
          <Table
            columns={columns}
            dataSource={declarations}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            loading={loading}
            scroll={{ x: 1200 }}
          />
        </Card>
      </Spin>

      <Modal
        title={editingDeclaration ? 'Edit Tax Declaration' : 'Create Tax Declaration'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={emptyValues}>
          {isSuperAdmin(user) && (
            <>
              <Form.Item
                label="Organization"
                name="organizationId"
                rules={[{ required: true, message: 'Please select organization' }]}
              >
                <Select
                  placeholder="Select organization"
                  onChange={handleOrganizationChange}
                  options={organizations.map((org) => ({
                    label: org.name,
                    value: org.id
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Employee"
                name="employeeId"
                rules={[{ required: true, message: 'Please select employee' }]}
              >
                <Select
                  placeholder="Select employee"
                  loading={employeesLoading}
                  disabled={employeesLoading || !selectedFormOrganizationId}
                  options={employees.map((emp) => ({
                    label: `${emp.firstName} ${emp.lastName} (${emp.employeeCode})`,
                    value: emp.id
                  }))}
                />
              </Form.Item>
            </>
          )}

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Fiscal Year"
                name="fy"
                rules={[{ required: true, message: 'Please enter fiscal year (e.g., 2023-24)' }]}
              >
                <Select
                  placeholder="Select FY"
                  options={[
                    { label: '2021-22', value: '2021-22' },
                    { label: '2022-23', value: '2022-23' },
                    { label: '2023-24', value: '2023-24' },
                    { label: '2024-25', value: '2024-25' },
                    { label: '2025-26', value: '2025-26' },
                    { label: '2026-27', value: '2026-27' }
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Tax Regime"
                name="regime"
                rules={[{ required: true, message: 'Please select regime' }]}
              >
                <Select
                  placeholder="Select regime"
                  options={[
                    { label: 'Old Regime', value: 'old' },
                    { label: 'New Regime', value: 'new' }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Card size="small" title="Deduction Details" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 0]}>
              {DEDUCTION_FIELDS.map((field) => (
                <Col xs={24} md={12} lg={12} key={field.name}>
                  <Form.Item
                    label={field.label}
                    name={field.name}
                    rules={[
                      {
                        validator(_, value) {
                          if (value === undefined || value === null || value === '') {
                            return Promise.resolve();
                          }

                          if (Number(value) < 0) {
                            return Promise.reject(new Error(`${field.label} must be non-negative`));
                          }

                          return Promise.resolve();
                        }
                      }
                    ]}
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder={field.placeholder}
                      formatter={(value) =>
                        `Rs. ${Number(value || 0).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}`
                      }
                      parser={(value) => Number(value.replace(/Rs\.\s?|(,*)/g, ''))}
                    />
                  </Form.Item>
                </Col>
              ))}
            </Row>

            <Card size="small" type="inner" style={{ marginTop: 16, backgroundColor: '#f5f5f5' }}>
              <Row>
                <Col span={12}>
                  <Typography.Text strong>Total Tax Benefits:</Typography.Text>
                </Col>
                <Col span={12} style={{ textAlign: 'right' }}>
                  <Typography.Text strong>{formatCurrency(getTotalDeductions(form.getFieldsValue()))}</Typography.Text>
                </Col>
              </Row>
            </Card>
          </Card>
        </Form>
      </Modal>
    </Layout>
  );
}

export default TaxDeclaration;
